import { z } from 'zod';

export const registerSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(50, 'Username must be at most 50 characters')

    // ❌ Only lowercase letters and numbers
    .refine((val) => /^[a-z0-9]+$/.test(val), {
      message: 'Username can contain only lowercase letters and numbers',
    })
    // ❌ Must not be only numbers
    .refine((val) => /[a-z]/.test(val), {
      message: 'Username must contain at least one lowercase letter',
    }),
  mobile: z
    .string()
    .min(1, 'Mobile number is required')
    .regex(/^\+[1-9]\d{1,14}$/, 'Mobile number must include country code (e.g., +919876543210)'),

  referralCode: z.string().optional().or(z.literal('')),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(14, 'Password must be at most 14 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  // agree: z.boolean().refine((val) => val === true, 'You must accept the terms and conditions'),
  ageConfirm: z.boolean().refine((val) => val === true, {
    message: 'You must confirm you are 18+',
  }),
});

export const loginSchema = z.object({
  username: z.string().nonempty('Username is required'),
  password: z.string().nonempty('Password is required'),
});

export const phoneSchema = z.object({
  mobile: z
    .string()
    .min(1, 'Mobile number is required')
    .max(20, 'Mobile number must be at most 20 digits')
    .regex(/^\+[1-9]\d{1,14}$/, 'Please enter a valid phone number'),
});

// Add Account

export const accountSchema = z.object({
  accountType: z.string().min(1, 'Account type is required'),
  bankName: z
    .string()
    .trim()
    .min(3, 'Bank name must be at least 3 characters')
    .max(50, 'Bank name must be at most 50 characters'),

  holderName: z
    .string()
    .trim()
    .min(3, 'Holder name must be at least 3 characters')
    .max(50, 'Holder name must be at most 50 characters'),
  accountNumber: z
    .string()
    .trim()
    .min(9, 'Account number must be at least 9 digits')
    .regex(/^\d+$/, 'Only numbers allowed'),
  ifscCode: z
    .string()
    .trim()
    .max(60, 'IFSC code must not exceed 60 characters')
    .regex(/^[a-zA-Z0-9._@\-!#$%^&*()+={}[\]:;"'<>,?/\\|~`]+$/, 'Contains invalid characters')
    .optional(),
});

export const upiSchema = z.object({
  name: z.string().trim().min(3, 'Name must be at least 3 characters'),
  upiId: z
    .string()
    .trim()
    .max(60, 'UPI ID must not exceed 60 characters')
    .regex(/^[a-zA-Z0-9._@\-!#$%^&*()+={}[\]:;"'<>,?/\\|~`]+$/, 'Contains invalid characters')
    .optional(),
  number: z
    .string()
    .trim()
    .max(20, 'Phone number (including +) must be at most 20 characters')
    .regex(/^\+?\d+$/, 'Phone number may start with + and must contain only digits'),
});

export const changePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Current password is required'),

    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(14, 'Password must be at most 14 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),

    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(14, 'Password must be at most 14 characters')
  .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Must contain at least one special character');

// 📱 Step 1: Send OTP
export const forgotPasswordSchema = z.object({
  mobile: z
    .string()
    .min(1, 'Mobile number is required')
    .regex(/^\+[1-9]\d{1,14}$/, 'Enter valid number with country code'),
});

// 🔐 Step 2: Reset Password
export const resetPasswordSchema = z
  .object({
    otp: z.string().regex(/^\d{6}$/, 'OTP must be exactly 6 digits'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;
